import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { IAudience, IUser } from '@/interfaces';
import React, { useEffect } from 'react';

import Grid from '@mui/material/Unstable_Grid2';
import { LoadingButton } from '@mui/lab';
import { SharedUser } from '@/types';
import { getUsers } from '@/api/audience';
import { useAuth0 } from '@auth0/auth0-react';

export interface ShareAudienceDialogProps {
  selectedAudience: IAudience;
  open: boolean;
  onClose: () => void;
  onShare: (sharedUsers: SharedUser[]) => void;
}

export const ShareAudienceDialog: React.FC<ShareAudienceDialogProps> = ({
  selectedAudience,
  open,
  onClose,
  onShare,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedUsers, setSelectedUsers] = React.useState<IUser[]>([]);
  const [usersList, setUsersList] = React.useState<IUser[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const accessToken = await getAccessTokenSilently();
      const { data, error } = await getUsers(accessToken);
      if (error) {
        console.error(error);
        setLoading(false);
        return;
      }
      if (data) {
        setLoading(false);
        setUsersList(data);
        if (
          selectedAudience.shared_with &&
          selectedAudience.shared_with.length > 0
        ) {
          const sharedUsers = data.filter((user) => {
            return selectedAudience.shared_with?.some(
              (sharedUser) =>
                sharedUser.username === user.user_id &&
                sharedUser.user_group === user.user_group
            );
          });

          setSelectedUsers(sharedUsers);
        } else {
          setSelectedUsers([]);
        }
      }
    };
    fetchData();
  }, [getAccessTokenSilently, selectedAudience]);

  const handleShare = () => {
    const sharedUsers = selectedUsers.map((user) => {
      return {
        username: user.user_id,
        user_group: user.user_group,
      };
    });

    onShare(sharedUsers);
  };

  return (
    <Dialog
      onClose={onClose}
      open={open}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '400px',
        },
      }}
    >
      <DialogTitle>Share '{selectedAudience.name}'</DialogTitle>
      <Grid xs={12} p={2} display={'flex'} flexDirection={'column'}>
        {loading ? (
          <Grid xs={12}>
            <Skeleton variant="rounded" height={56} />
          </Grid>
        ) : (
          <Grid xs={12}>
            <Autocomplete
              disableCloseOnSelect
              multiple
              options={usersList.sort((a, b) =>
                a.user_group.localeCompare(b.user_group)
              )}
              getOptionLabel={(option) => option.user_name}
              value={selectedUsers}
              groupBy={(option) => option.user_group}
              onChange={(_, newValue) => {
                setSelectedUsers(newValue);
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={`${option.user_name} (${option.user_group})`}
                    color="primary"
                    variant={'outlined'}
                    avatar={
                      <img
                        src={option.user_image}
                        alt={option.user_name}
                        style={{ width: 20, height: 20, borderRadius: '50%' }}
                      />
                    }
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box
                  component="li"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  {...props}
                >
                  <img
                    src={option.user_image}
                    alt={option.user_name}
                    style={{
                      width: 35,
                      height: 35,
                      borderRadius: '50%',
                      marginRight: 8,
                    }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant={'body1'}>
                      {option.user_name}
                    </Typography>
                    <Typography variant={'body2'} color={'textSecondary'}>
                      {option.user_email}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={selectedUsers.length === 0 ? 'Select users' : ''}
                />
              )}
            />
          </Grid>
        )}

        <Grid xs={12} mt={2} display={'flex'} justifyContent={'end'}>
          <LoadingButton
            variant={'contained'}
            size={'small'}
            disabled={loading}
            onClick={handleShare}
          >
            Share Audience
          </LoadingButton>
        </Grid>
      </Grid>
    </Dialog>
  );
};
